import { useState } from "react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ChevronDown, User, Bot, MessageSquare, Loader2 } from "lucide-react";
import { isNotepadSession } from "@/lib/notepad-transcript";
import { NotepadMessageList } from "./NotepadMessageList";
import type { Message, Session } from "./types";

interface MessageListProps {
  session: Session | undefined;
  isLoading: boolean;
  formatMessageTime: (session: Session) => string;
  /** The user whose sessions these are; needed to read a deferred notepad conversation. */
  userId?: string;
}

const SystemPrompt = ({ message }: { message: Message }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl">
        <button
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span>System Prompt</span>
          <ChevronDown
            className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </button>
        {isExpanded && (
          <div className="mt-2 overflow-hidden rounded-lg border bg-muted/50 p-3 text-xs text-muted-foreground">
            {message.content.map((block, i) => (
              <p key={i} className="whitespace-pre-wrap">
                {block.text}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const MessageList = ({
  session,
  isLoading,
  formatMessageTime,
  userId,
}: MessageListProps) => {
  if (isLoading) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="mt-3 text-xs text-muted-foreground">
          Loading conversation...
        </p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center text-center">
        <MessageSquare className="h-8 w-8 text-muted-foreground/50" />
        <h3 className="mt-3 text-sm font-medium">No conversations</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          No chat sessions found for this user.
        </p>
      </div>
    );
  }

  // A notepad bite keeps its conversation in notepad_messages; its classic
  // messages hold only the system prompt.
  if (isNotepadSession(session)) {
    return (
      <div className="mx-auto max-w-3xl space-y-2">
        {session.messages[0] && <SystemPrompt message={session.messages[0]} />}
        <NotepadMessageList session={session} userId={userId} />
      </div>
    );
  }

  const messageTime = formatMessageTime(session);

  return (
    <div className="mx-auto max-w-3xl space-y-2">
      {session.messages.map((msg, idx) => {
        // System prompt - collapsible
        if (idx === 0) {
          return <SystemPrompt key={idx} message={msg} />;
        }

        const isUser = msg.role !== "assistant";

        return (
          <div
            key={idx}
            className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}
          >
            {/* Avatar */}
            <Avatar className="size-7 shrink-0 mt-0.5">
              <AvatarFallback className="bg-muted text-xs overflow-hidden">
                {isUser ? (
                  <User className="h-3.5 w-3.5" />
                ) : (
                  <Bot className="h-3.5 w-3.5" />
                )}
              </AvatarFallback>
            </Avatar>

            {/* Message Bubble */}
            <div
              className={`flex max-w-[75%] flex-col ${isUser ? "items-end" : "items-start"}`}
            >
              <div
                className={`overflow-hidden px-3 py-2 ${
                  isUser
                    ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md"
                    : "bg-muted rounded-2xl rounded-bl-md"
                }`}
              >
                <div className="text-xs leading-relaxed">
                  {msg.content.map((block, i) => (
                    <p key={i} className="whitespace-pre-wrap break-words">
                      {block.text}
                    </p>
                  ))}
                </div>
              </div>
              {messageTime && (
                <span className="mt-1 text-xs text-muted-foreground">
                  {messageTime}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
